'use client';

import type { CategoryProductFragment } from '@business/gql/graphql';
import { CampaignInfo } from '@lib/services/contentful/getCampaigns';
import type { Dictionary } from '@ts/dictionary';
import PrefetchLink from '@ui/components/Link/PrefetchLink';
import { PageGTMInfo } from '@ui/components/Product/ProductSearch';
import ProductCard from '@ui/components/ProductCard/ProductCard';
import ProductCardBadges from '@ui/components/ProductCard/ProductCardBadges';
import Thumbnail from '@ui/components/Thumbnail/Thumbnail';
import { market } from '@utils/config';
import currencies from '@utils/currency/currencies';
import { gTagSelectListItem } from '@utils/googleTags';
import type { Maybe } from 'graphql/jsutils/Maybe';
import { Suspense } from 'react';

export type ProductContentProps = Maybe<CategoryProductFragment>;

type ProductContentWithPriority = ProductContentProps & {
  priority?: boolean;
};

function getAlternateSrc(product: CategoryProductFragment): string | undefined {
  if (!product.media?.length || product.media.length < 2) {
    return undefined;
  }
  if (product.type === 'Variant' && product.media.at(2)) {
    return product.media.at(2)?.src;
  }
  return product.media.at(1)?.src;
}

type Props = {
  data: ProductContentProps | ProductContentWithPriority;
  dictionary: Dictionary;
  gtmInfo: PageGTMInfo;
  campaignInfo?: CampaignInfo;
};

export default function ProductContent(props: Props) {
  const { data: item, dictionary, gtmInfo, campaignInfo } = props;

  let priority = false;

  if (!item) return;

  if ('priority' in item) {
    priority = item.priority ?? false;
  }
  const secondImageURL = getAlternateSrc(item);

  return (
    <PrefetchLink
      data-testid="ProductContent-PrefetchLink"
      href={item?.slug ?? ''}
      onClick={() => gTagSelectListItem(item, currencies[market], gtmInfo)}>
      <ProductCard
        key={item?.id}
        productId={item.id}
        secondaryImage={secondImageURL}
        Badges={
          <ProductCardBadges
            campaignInfo={campaignInfo}
            product={item}
            dictionary={dictionary}
          />
        }
        title={item?.title ?? ''}
        brand={item?.brand ?? ''}
        priceDetails={{
          priceType: item?.priceType,
          fromPrice: item?.fromPrice ?? undefined,
          price: item?.price,
          presentationPrice: item?.presentationPrice,
          presentationOriginal: item?.presentationOriginal ?? undefined,
          presentationRrpWhenCampaign:
            item?.presentationRrpWhenCampaign ?? undefined,
          rrpWhenCampaign: item?.rrpWhenCampaign ?? undefined,
          originalPrice: item?.originalPrice ?? undefined,
        }}
        priceDictionary={{
          from: dictionary?.from,
          recommendedPrice: dictionary?.recommendedPrice,
        }}
        stockStatus={{
          stockStatus: item.stockStatus,
          nextDeliveryDate: item.nextDeliveryDate,
        }}
        stockStatusDictionary={dictionary}
        moreProductVariants={
          item?.hasVariants ? dictionary?.moreOptions : undefined
        }
        productType={item.type === 'Variant' ? 'Variant' : 'Product'}>
        <Suspense>
          <Thumbnail
            title={item?.title ?? ''}
            src={item?.media[0]?.src}
            data={undefined}
            priority={priority}
            alternateSrc={secondImageURL}
          />
        </Suspense>
      </ProductCard>
    </PrefetchLink>
  );
}
